import type { HostSlotReasonCode } from './hostSlotPort.js';
import type { Telemetry } from './telemetry.js';

/** A failure with the reason code the slot and telemetry report. */
export class LoaderError extends Error {
  readonly reason: HostSlotReasonCode;
  /** A narrower reason for the fault report than the slot's reason code. */
  readonly faultReason?: string;

  constructor(
    reason: HostSlotReasonCode,
    message: string,
    options?: { cause?: unknown; faultReason?: string },
  ) {
    super(message, options);
    this.name = 'LoaderError';
    this.reason = reason;
    this.faultReason = options?.faultReason;
  }
}

export function isLoaderError(error: unknown): error is LoaderError {
  return error instanceof LoaderError;
}

/** Keeps a typed error as is and wraps anything else with the given reason. */
export function toLoaderError(
  error: unknown,
  reason: HostSlotReasonCode,
  message: string,
): LoaderError {
  return isLoaderError(error)
    ? error
    : new LoaderError(reason, message, { cause: error });
}

export interface ModuleAttribution {
  readonly slot: string;
  readonly moduleId: string;
  readonly version?: string;
}

export type ErrorEventTarget = Pick<
  EventTarget,
  'addEventListener' | 'removeEventListener'
>;

interface Tracked {
  readonly url: string;
  readonly attribution: ModuleAttribution;
}

/**
 * Attributes uncaught errors and unhandled rejections to the module file they
 * came from. The page-level listeners are installed only while a module is
 * mounted, and never stop the error from reaching the page's own handlers.
 */
export class AsyncErrorAttributor {
  private readonly tracked = new Set<Tracked>();

  constructor(
    private readonly telemetry: Telemetry,
    private readonly target: ErrorEventTarget | undefined,
  ) {}

  track(url: string, attribution: ModuleAttribution): () => void {
    const entry: Tracked = { url, attribution };
    this.tracked.add(entry);
    if (this.tracked.size === 1) {
      this.target?.addEventListener('error', this.onError);
      this.target?.addEventListener('unhandledrejection', this.onRejection);
    }
    return () => {
      if (!this.tracked.delete(entry)) {
        return;
      }
      if (this.tracked.size === 0) {
        this.target?.removeEventListener('error', this.onError);
        this.target?.removeEventListener(
          'unhandledrejection',
          this.onRejection,
        );
      }
    };
  }

  private readonly onError = (event: Event): void => {
    const { filename, error } = event as {
      filename?: unknown;
      error?: unknown;
    };
    if (typeof filename !== 'string') {
      return;
    }
    this.report(error ?? new Error('Uncaught error in module'), (url) =>
      sameFile(url, filename),
    );
  };

  private readonly onRejection = (event: Event): void => {
    const { reason } = event as { reason?: unknown };
    const stack =
      typeof reason === 'object' && reason !== null && 'stack' in reason
        ? String((reason as { stack?: unknown }).stack)
        : '';
    if (stack === '') {
      return;
    }
    this.report(reason, (url) => stack.includes(url));
  };

  private report(error: unknown, matches: (url: string) => boolean): void {
    for (const { url, attribution } of Array.from(this.tracked)) {
      if (matches(url)) {
        this.telemetry.error(error, { ...attribution, reason: 'module-error' });
        return;
      }
    }
  }
}

function sameFile(url: string, filename: string): boolean {
  return url === filename || stripQuery(url) === stripQuery(filename);
}

function stripQuery(value: string): string {
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
}
